import { createSelector } from "@ngrx/store"
import { makeMapFromListOfObjectsAndKey, sortByProp } from "globals/helpers/helpers"
import { AppTableData, SurveyResponseColumnData } from "../../helpers/table-column-types"
import { tableLightColors } from "globals/helpers/table-helpers.data"
import { AppNotification, AppSurvey, JoiApp, JoiAppInfo, SurveySelectData, SurveySubmission } from "globals/store/app.data"
import { Dictionary } from "@ngrx/entity"
import { getAllTimezoneIds, getListOfTimezonesUsed } from "./programs.reselector"
import { MAX_ENTITY_STARS } from "globals/helpers/constants"
import { tableTemplateIds, twoDeepTextSorter, twoDeepSimpleSorter, customQuestionColumnFromQuestion, publishDotMenuIds } from "../../helpers/table-helpers"
import { TableColumn } from "../../helpers/table.data"
import { JoiFile } from "globals/store/files.model"
import { CustomQuestion } from "globals/store/events.data"
import { getFileAnswer } from "./entity.reselector"
import { DropdownOption } from "globals/store/programs.data"

const apps = (store) => store.apps.entities
const appNotificationsById = (store) => store.apps.notificationsByAppId
const appResponses = (store) => store.apps.responses
const appSearchTerm = (store) => store.filters.appTable.search

export const appList = createSelector(apps, (apps: JoiApp) => Object.values(apps).sort(sortByProp('title')))
const USER_LOAD_PERIOD = 24 * 60 * 60 * 1000

export const appNotificationsReselector = createSelector(
  appNotificationsById,
  (appNotificationsById: Dictionary<Dictionary<AppNotification>>, appId: string) => Object.values(appNotificationsById[appId] || {}).sort(sortByProp('pushTime', 1))
)

export const appUserCountReselector = createSelector(
  apps,
  (apps: Dictionary<JoiAppInfo>, appId: string) => {
    const appInfo = apps[appId]
    const now = new Date().getTime()
    return Object.values(appInfo.fakeUsers).filter(u => now - u.lastLoad < USER_LOAD_PERIOD).length
  }
)

export const appInstructionsReselector = createSelector(
  apps,
  (apps: Dictionary<JoiAppInfo>, appId: string) => {
    const appInfo = apps[appId]
    const instructions = `Search for Joi Events in the app store
Enter the event app name:\n${appInfo.title}${ appInfo.password && appInfo.passwordProtected ? ('\nEnter the password:\n' + appInfo.password) : ''}
And you will be in the app

Have a great event`
    return instructions
  }
)

export const appsTableReselector = createSelector(
  appList,
  appSearchTerm,
  (apps, searchTerm: string) => {
    // console.log({ accountList })
        
    searchTerm = searchTerm ? searchTerm.toLocaleUpperCase() : null
    const filteredApps = !searchTerm ? apps : apps.filter(a => a.title.toLocaleUpperCase().includes(searchTerm) || a.description.toLocaleUpperCase().includes(searchTerm))

    const rows: AppTableData[] = filteredApps.map(app => {
      const activeDotMenuOptions = new Set(publishDotMenuIds)
      activeDotMenuOptions.delete('edit')
      activeDotMenuOptions.delete('duplicate')
      if (app.status !== 'published') activeDotMenuOptions.delete('update')

      const { id, title, description, creator, createdAt, status } = app
      const appRow: AppTableData = {
        app,
        id,
        name: { text: title, bold: true },
        description: { text: description || '' },
        status: {
          percent: status === "published" ? 1 : 0,
          lights: [{
            text: status === 'published' ? 'Active' : 'Inactive',
            color: status === 'published' ? tableLightColors.green  : tableLightColors.gray,
          }]},
        created: !createdAt ? null : { updater: creator, lastUpdated: createdAt },
        updated: null,
        activeDotMenuOptions
      }
      return appRow
    })
    const rowsById = makeMapFromListOfObjectsAndKey(rows, 'id')
    return { rows, rowsById, rowCountBeforeFiltering: apps.length }
  }
)

export const notificationReselector = createSelector(
  appNotificationsById,
  (appNotificationsById: Dictionary<Dictionary<AppNotification>>, {appId, notificationId }: { appId: string, notificationId: string }): AppNotification => (appNotificationsById[appId] || {})[notificationId]
)

export const getNotificationListOfTimeZonesUsed = createSelector(
  appNotificationsById,
  getAllTimezoneIds,
  (appNotificationsById: Dictionary<Dictionary<AppNotification>>, timezoneIds: Set<string>, {appId, notificationId }: { appId: string, notificationId: string }) => {
    const notification = (appNotificationsById[appId] || {})[notificationId]
    const date = new Date(notification.pushTime)
    const timezones = getListOfTimezonesUsed(timezoneIds, date)
    console.log({ date, timezones })
    return timezones
  }
)

export const surveyReselector = createSelector(
  apps,
  (apps: Dictionary<JoiAppInfo>, d: SurveySelectData): AppSurvey => {
    const survey = apps[d.appId].surveys[d.surveyId]
    console.log({ d, survey })
    return survey
  }
)

export const getSurveyListOfTimeZonesUsed = createSelector(
  apps,
  getAllTimezoneIds,
  (apps: Dictionary<JoiAppInfo>,timezoneIds: Set<string>, d: SurveySelectData) => {
    const date = new Date(apps[d.appId].surveys[d.surveyId].pushTime)
    const timezones = getListOfTimezonesUsed(timezoneIds, date)
    return timezones
  }
)


export const surveyDropdownReselector = createSelector(
  apps,
  (apps: Dictionary<JoiAppInfo>, appId: string): DropdownOption[] => {
    const app = apps[appId]
    if (!app) return []

    const options: DropdownOption[] = Object.values(app.surveys).map((s => ({ id: s.id, title: s.title })))
    return options
  }
)

export const surveyResponsesTableReselector = createSelector(
  apps,
  appResponses,
  (apps: Dictionary<JoiAppInfo>, appResponses: Dictionary<SurveySubmission[]>, {appId, surveyId }: { appId: string, surveyId: string }): { columns: TableColumn[], items: SurveyResponseColumnData[], questions: CustomQuestion[] } => {

    const emptyData = { columns: [], items: [], questions: [] }
    const app = apps[appId]
    if (!app) return emptyData

    const survey = app.surveys[surveyId]
    if (!survey) return emptyData

    const responses = appResponses[surveyId]
    if (!responses) return emptyData

    const columns: TableColumn[] = !app.nameAndEmail ? [] : [{
      title: 'Name',
      id: 'name',
      templateId: tableTemplateIds.boldText,
      sortingFunction: twoDeepTextSorter('name', 'text'),
    },{
      title: 'Email',
      id: 'email',
      templateId: tableTemplateIds.tableEmail,
      sortingFunction: twoDeepTextSorter('email', 'text'),
    }]
    
    survey.customQuestions.map(customQuestionColumnFromQuestion).forEach(c => columns.push(c))  
    const items: SurveyResponseColumnData[] = responses.map(r => {
      const item: SurveyResponseColumnData = {
        id: r.id,
        name: { text: r.name || `No name given`, bold: true },
        email: { text: r.email || 'No email given' }
      }
      survey.customQuestions.forEach(q => {
        const answer = r.customQuestions[q.id]
        const fullyNotApplicable = !answer
        item[q.id] = q.type === 'link' ? { url: fullyNotApplicable ? 'N/A' : getFileAnswer(answer as JoiFile[]), NA: fullyNotApplicable } : 
          q.type === 'stars' ? (!answer ? null : { stars: answer, maxStars: MAX_ENTITY_STARS }) :
        { text: fullyNotApplicable ? 'N/A' :
          q.type === 'checkbox' ? (answer ? 'checked' : 'unchecked') :
          q.type === 'radio' ? answer || 'unselected' : answer || '' }
      })
      return item
    })

    console.log({ columns, items, questions: survey.customQuestions })

    return { columns, items, questions: survey.customQuestions }
  }
)
